import { useMemo } from 'react';
import { Group } from '../../../types/groups';
import { TableColumn } from '../../shared/table';
import { IconAvatar } from '../../shared';
import { StatusTag } from '../../shared/table';
import { getInteractionIcon, getVisibilityIcon } from '../../../utils/iconHelpers';

export const useGroupsTableConfig = (): TableColumn<Group>[] => {
  return useMemo<TableColumn<Group>[]>(() => [
    {
      header: 'Name',
      width: '250px',
      flexGrow: true,
      renderCell: (group) => (
        <div className="flex items-center">
          <div className="mr-3">
            <IconAvatar 
              iconName={group.typeIcon} 
              avatarUrl={group.avatarUrl}
            />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold">
              {group.name}
            </span>
            {group.department && (
              <span className="text-xs text-gray-500">{group.department}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      header: 'Type',
      width: '150px',
      renderCell: (group) => (
        group.type ? (
          <StatusTag label={group.type} dataTarget={`group-type-${group.id}`} />
        ) : null
      ),
    },
    {
      header: 'Leader',
      width: '180px',
      renderCell: (group) => <span className="text-sm">{group.leader}</span>,
    },
    {
      header: 'Members',
      width: '60px',
      align: 'right',
      renderCell: (group) => (
        <span className="tabular-nums">{group.memberCount}</span>
      ),
    },
    {
      header: 'Capacity',
      width: '60px',
      renderCell: (group) => (
        <StatusTag 
          variant={group.isFull ? 'info-orange' : 'info'} 
          label={group.isFull ? 'Full' : 'Open'} 
          dataTarget={`group-capacity-${group.id}`}
        />
      ),
    },
    {
      header: 'Interaction',
      width: '120px',
      renderCell: (group) => (
        <div className="flex items-center gap-3">
          <div title={group.interaction}>
            {getInteractionIcon(group.interaction)}
          </div>
          <div title={group.visibility === 'public' ? 'Public' : 'Internal'}>
            {getVisibilityIcon(group.visibility)}
          </div>
        </div>
      ),
    },
    {
      header: 'Status',
      width: '60px',
      renderCell: (group) => (
        <StatusTag 
          variant={group.status === 'Active' ? 'success' : 'default'} 
          label={group.status} 
          dataTarget={`group-status-${group.id}`}
        />
      ),
    },
  ], []);
};
